import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTutorial } from '@/lib/TutorialContext';

// Calculate tooltip position based on target element
const calculatePosition = (targetRect, position, tooltipSize) => {
  const padding = 12;
  const arrowSize = 8;

  if (!targetRect) {
    // Center in viewport for no target
    return {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)'
    };
  }

  const positions = {
    top: {
      top: targetRect.top - tooltipSize.height - padding - arrowSize,
      left: targetRect.left + targetRect.width / 2,
      transform: 'translateX(-50%)'
    },
    bottom: {
      top: targetRect.bottom + padding + arrowSize,
      left: targetRect.left + targetRect.width / 2,
      transform: 'translateX(-50%)'
    },
    left: {
      top: targetRect.top + targetRect.height / 2,
      left: targetRect.left - tooltipSize.width - padding - arrowSize,
      transform: 'translateY(-50%)'
    },
    right: {
      top: targetRect.top + targetRect.height / 2,
      left: targetRect.right + padding + arrowSize,
      transform: 'translateY(-50%)'
    }
  };

  const pos = positions[position] || positions.bottom;

  // Ensure tooltip stays within viewport
  const viewport = {
    width: window.innerWidth,
    height: window.innerHeight
  };

  // Clamp values
  let finalTop = typeof pos.top === 'number' ? pos.top : parseInt(pos.top);
  let finalLeft = typeof pos.left === 'number' ? pos.left : parseInt(pos.left);

  // Adjust for viewport bounds
  if (position === 'top' || position === 'bottom') {
    finalLeft = Math.max(padding, Math.min(finalLeft, viewport.width - tooltipSize.width / 2 - padding));
  }
  if (position === 'left' || position === 'right') {
    finalTop = Math.max(padding, Math.min(finalTop, viewport.height - tooltipSize.height / 2 - padding));
  }

  return {
    top: finalTop,
    left: finalLeft,
    transform: pos.transform
  };
};

// Arrow component pointing to target
const Arrow = ({ position }) => {
  const arrowClasses = {
    top: 'bottom-[-6px] left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-white',
    bottom: 'top-[-6px] left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-white',
    left: 'right-[-6px] top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-white',
    right: 'left-[-6px] top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-white'
  };

  return (
    <div
      className={`absolute w-0 h-0 border-[6px] ${arrowClasses[position] || arrowClasses.bottom}`}
    />
  );
};

// Set to true when tutorial is fully implemented (branch_admin nav targets aligned with TutorialContext)
const TUTORIAL_OVERLAY_ENABLED = false;

export default function TutorialOverlay() {
  const {
    isActive,
    currentStep,
    currentStepData,
    totalSteps,
    nextStep,
    prevStep,
    skipTutorial
  } = useTutorial();

  if (!TUTORIAL_OVERLAY_ENABLED) return null;

  const [targetRect, setTargetRect] = useState(null);
  const [tooltipSize, setTooltipSize] = useState({ width: 320, height: 200 });
  const tooltipRef = useRef(null);

  // Find and track the target element
  useEffect(() => {
    if (!isActive || !currentStepData?.target) {
      setTargetRect(null);
      return;
    }

    const findTarget = () => {
      const target = document.querySelector(currentStepData.target);
      if (target) {
        const rect = target.getBoundingClientRect();
        setTargetRect(rect);
      } else {
        setTargetRect(null);
      }
    };

    // Initial find
    findTarget();

    // Re-find on scroll/resize
    window.addEventListener('scroll', findTarget, true);
    window.addEventListener('resize', findTarget);

    // Poll for element (in case it's rendered later)
    const pollInterval = setInterval(findTarget, 100);

    return () => {
      window.removeEventListener('scroll', findTarget, true);
      window.removeEventListener('resize', findTarget);
      clearInterval(pollInterval);
    };
  }, [isActive, currentStepData]);

  // Measure tooltip size
  useEffect(() => {
    if (tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      setTooltipSize({ width: rect.width, height: rect.height });
    }
  }, [currentStepData]);

  if (!isActive || !currentStepData) return null;

  const position = currentStepData.position || 'bottom';
  const isCentered = !currentStepData.target;
  const tooltipPosition = calculatePosition(targetRect, position, tooltipSize);

  const overlayContent = (
    <AnimatePresence>
      {isActive && (
        <>
          {/* Dark overlay with spotlight cutout */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9998]"
            onClick={skipTutorial}
          >
            {/* SVG mask for spotlight effect */}
            <svg className="absolute inset-0 w-full h-full">
              <defs>
                <mask id="spotlight-mask">
                  <rect x="0" y="0" width="100%" height="100%" fill="white" />
                  {targetRect && (
                    <rect
                      x={targetRect.left - 8}
                      y={targetRect.top - 8}
                      width={targetRect.width + 16}
                      height={targetRect.height + 16}
                      rx="8"
                      fill="black"
                    />
                  )}
                </mask>
              </defs>
              <rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                fill="rgba(0, 0, 0, 0.75)"
                mask="url(#spotlight-mask)"
              />
            </svg>

            {/* Spotlight border highlight */}
            {targetRect && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute rounded-lg ring-2 ring-blue-400 ring-offset-2 pointer-events-none"
                style={{
                  top: targetRect.top - 8,
                  left: targetRect.left - 8,
                  width: targetRect.width + 16,
                  height: targetRect.height + 16
                }}
              />
            )}
          </motion.div>

          {/* Tooltip */}
          <motion.div
            ref={tooltipRef}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="fixed z-[9999] w-80 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: tooltipPosition.transform
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Arrow */}
            {!isCentered && <Arrow position={position} />}

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-white" />
                <span className="text-white text-sm font-medium">
                  Krok {currentStep + 1} z {totalSteps}
                </span>
              </div>
              <button
                onClick={skipTutorial}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                {currentStepData.title}
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                {currentStepData.description}
              </p>
            </div>

            {/* Footer */}
            <div className="px-4 pb-4 flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={skipTutorial}
                className="text-slate-500 hover:text-slate-700"
              >
                Pomiń
              </Button>

              <div className="flex items-center gap-2">
                {currentStep > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={prevStep}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Wstecz
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={nextStep}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                >
                  {currentStep < totalSteps - 1 ? (
                    <>
                      Dalej
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </>
                  ) : (
                    'Zakończ'
                  )}
                </Button>
              </div>
            </div>

            {/* Progress dots */}
            <div className="px-4 pb-3 flex justify-center gap-1">
              {Array.from({ length: totalSteps }).map((_, index) => (
                <div
                  key={index}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    index === currentStep
                      ? 'bg-blue-600'
                      : index < currentStep
                        ? 'bg-blue-300'
                        : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  // Render in portal to ensure it's on top
  return createPortal(overlayContent, document.body);
}
